import { Box, Button, useTheme, Stack, IconButton } from '@mui/material';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import Form from 'components/Form';
import useI18n from 'hooks/useI18n';
import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Close } from '@mui/icons-material';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import styled from 'styled-components';
import useUser from 'store/user/useUser';

declare const PuzzleSDK: any;

interface Props {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  disabled?: boolean;
  isTestMode?: boolean;
}

// 부모 컴포넌트에서 호출할 수 있는 메서드 정의
export interface NarrativeRecordHandle {
  clearDraft: () => void;
}

const NarrativeRecord = forwardRef<NarrativeRecordHandle, Props>(
  ({ register, setValue, watch, disabled, isTestMode }, ref) => {
    const i18n = useI18n();
    const { palette } = useTheme();
    const [voiceOn, setVoiceOn] = useState<boolean>(false);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const puzzleWsRef = useRef<any>(null);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
    const { student_id } = useUser();

    // 중간 저장용 임시 데이터 (세션 스토리지 키)
    const DRAFT_KEY = `narrative_draft_${student_id}`;

    // 부모에서 호출할 수 있는 메서드 노출
    useImperativeHandle(ref, () => ({
      clearDraft: () => {
        sessionStorage.removeItem(DRAFT_KEY);
        setValue('narrativeNote', '', { shouldValidate: true });
      },
    }));

    // 컴포넌트 마운트 시 임시 저장 데이터 복원
    useEffect(() => {
      const draft = sessionStorage.getItem(DRAFT_KEY);
      if (draft) {
        setValue('narrativeNote', draft, { shouldValidate: true });
      }
    }, []);

    // 음성 인식 결과를 처리하는 콜백 함수
    const sttCallback = (stt: string) => {
      if (!stt) return;
    };

    // TextArea onChange 핸들러 - 중간 저장 포함
    const handleTextAreaChange = (
      e: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      const value = e.target.value;
      setValue('narrativeNote', value, {
        shouldValidate: true,
      });
      // 세션 스토리지에 중간 저장
      sessionStorage.setItem(DRAFT_KEY, value);
    };

    // textAreaRef의 값을 감시하는 useEffect
    useEffect(() => {
      const interval = setInterval(() => {
        if (
          textAreaRef.current &&
          textAreaRef.current.value !== watch('narrativeNote')
        ) {
          const value = textAreaRef.current.value;
          setValue('narrativeNote', value, {
            shouldValidate: true,
          });
          // 세션 스토리지에 중간 저장
          sessionStorage.setItem(DRAFT_KEY, value);
        }
      }, 100);

      return () => clearInterval(interval);
    }, [setValue, watch, DRAFT_KEY]);

    // WebSocket 연결 상태를 처리하는 콜백 함수
    const connectedCallback = (status: boolean) => {
      console.log('connectedCallback:', status);
      setIsConnected(status);
    };

    const initializePuzzleSDK = () => {
      try {
        if (!PuzzleSDK) {
          console.error('PuzzleSDK is not loaded');
          return false;
        }

        const uri = process.env.REACT_APP_PUZZLE_AI_WS_URL;
        const appKey = process.env.REACT_APP_PUZZLE_AI_APP_KEY;
        const userId = student_id;
        const modelLanguage = 'ENG';
        const channels = 1;
        const isMonoModeInStereoModel = false;

        const fullUri = `${uri}?appKey=${encodeURIComponent(
          appKey || ''
        )}&userId=${encodeURIComponent(
          userId || ''
        )}&modelLanguage=${encodeURIComponent(
          modelLanguage
        )}&channels=${encodeURIComponent(
          channels.toString()
        )}&isMonoModeInStereoModel=${encodeURIComponent(
          isMonoModeInStereoModel.toString()
        )}`;

        PuzzleSDK.createInstance(fullUri, connectedCallback, sttCallback);

        return true;
      } catch (error) {
        console.error('Failed to initialize PuzzleSDK:', error);
        return false;
      }
    };

    const handleVoiceConnect = () => {
      try {
        if (!puzzleWsRef.current) {
          const initialized = initializePuzzleSDK();
          if (!initialized) return;

          puzzleWsRef.current = PuzzleSDK.getInstance();
          puzzleWsRef.current.addBoard('narrative', '서술기록');
          puzzleWsRef.current.addItem('narrative', 'narrativeNote', '서술');
        }

        puzzleWsRef.current.restartRecord();
        setIsConnected(true);
      } catch (error) {
        console.error('Failed to connect:', error);
        setIsConnected(false);
      }
    };

    const handleVoiceDisconnect = () => {
      try {
        if (puzzleWsRef.current) {
          puzzleWsRef.current.stopRecord();
          setIsConnected(false);

          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to disconnect:', error);
      }
    };

    const handleVoiceRestart = () => {
      try {
        if (puzzleWsRef.current) {
          puzzleWsRef.current.stopRecord();
          setTimeout(() => {
            puzzleWsRef.current.restartRecord();
          }, 200);
        }
      } catch (error) {
        console.error('Failed to restart:', error);
      }
    };

    const VoiceMenuOnHandler = () => {
      setVoiceOn(true);
    };

    const VoiceMenuOFFHandler = () => {
      handleVoiceDisconnect();
      setVoiceOn(false);
    };

    const openManual = () => {
      window.open(
        'https://smartnurse.notion.site/What-is-Voice-EMR-26272847337280e9ac36f6ef62ca69a8?pvs=74',
        '_blank'
      );
    };

    const handlePreventEvent = (e: React.ClipboardEvent | React.MouseEvent) => {
      if (isTestMode) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 브라우저 탭 닫을 때 임시 저장 데이터 삭제 (새로고침 제외)
    useEffect(() => {
      const UNLOAD_FLAG_KEY = `${DRAFT_KEY}_unload_flag`;

      const handleBeforeUnload = () => {
        // 새로고침인지 탭 닫기인지 구분하기 위한 플래그 설정
        sessionStorage.setItem(UNLOAD_FLAG_KEY, 'true');
      };

      const handleLoad = () => {
        // 페이지 로드 시 플래그 확인
        const wasUnload = sessionStorage.getItem(UNLOAD_FLAG_KEY);
        if (wasUnload === 'true') {
          // 새로고침이었음 - 플래그만 제거
          sessionStorage.removeItem(UNLOAD_FLAG_KEY);
        }
      };

      const handleVisibilityChange = () => {
        // 탭이 숨겨지고(hidden) 플래그가 있으면 실제 탭 닫기로 판단
        if (document.visibilityState === 'hidden') {
          const wasUnload = sessionStorage.getItem(UNLOAD_FLAG_KEY);
          if (wasUnload === 'true') {
            // 잠시 후에도 페이지가 로드되지 않으면 탭 닫기로 판단
            setTimeout(() => {
              if (document.visibilityState === 'hidden') {
                sessionStorage.removeItem(DRAFT_KEY);
                sessionStorage.removeItem(UNLOAD_FLAG_KEY);
              }
            }, 100);
          }
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('load', handleLoad);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // 초기 로드 시 체크
      handleLoad();

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('load', handleLoad);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
        if (puzzleWsRef.current) {
          handleVoiceDisconnect();
        }
      };
    }, [DRAFT_KEY]);

    return (
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Button
            variant="contained"
            sx={{ minWidth: '15px', padding: '6px 7px' }}
            onClick={VoiceMenuOnHandler}
          >
            <SettingsVoiceIcon
              fontSize="small"
              sx={{
                color: 'white',
                height: '18px',
                animation: isConnected ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          </Button>
          {!voiceOn ? null : (
            <Box
              sx={{
                border: `1px solid ${palette.primary.main}`,
                borderRadius: '4px',
                marginLeft: '10px',
                display: 'flex',
                alignItems: 'center',
                height: '30px',
              }}
            >
              <Button
                sx={{ fontSize: '12px' }}
                onClick={handleVoiceConnect}
                disabled={isConnected}
              >
                ДУУГ АСААХ
              </Button>
              <Button
                sx={{ fontSize: '12px' }}
                onClick={handleVoiceRestart}
                disabled={!isConnected}
              >
                ДАХИН ЭХЛҮҮЛЭХ
              </Button>
              <Button
                sx={{ fontSize: '12px' }}
                onClick={handleVoiceDisconnect}
                disabled={!isConnected}
              >
                ДУУГ УНТРААХ
              </Button>
              <Button sx={{ fontSize: '12px' }} onClick={openManual}>
                ГАРЫН АВЛАГА
              </Button>

              <a
                href="https://puzzle-ai.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{ fontSize: '12px' }}>
                  <span style={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                    Powered by
                  </span>
                  <span style={{ fontWeight: 'bold' }}> PuzzleAI</span>
                </div>
              </a>

              <IconButton size="small" onClick={VoiceMenuOFFHandler}>
                <Close color="primary" sx={{ height: '18px' }} />
              </IconButton>
            </Box>
          )}
        </Box>
        <Form.Item label={i18n('NARRATIVE.NARRATIVENOTE')}>
          <TextArea
            id="narrativeNote"
            ref={textAreaRef}
            defaultValue=""
            value={watch('narrativeNote')}
            onChange={handleTextAreaChange}
            aria-multiline="true"
            onPaste={handlePreventEvent}
            onCopy={handlePreventEvent}
            onContextMenu={handlePreventEvent}
            rows={20}
          />
        </Form.Item>
      </Stack>
    );
  }
);

NarrativeRecord.displayName = 'NarrativeRecord';

export default NarrativeRecord;

const TextArea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 10px;
  outline: none;
  border-radius: 4px;
  resize: none;
  font-size: 14px;
  font-weight: 400;
`;
